import { COLORS } from '@/constants';
import styled from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const HeroBanner = styled.div`
  width: 100%;
  height: 250px;
  overflow: hidden;
  background: ${COLORS['neutral/200']};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const ContentCard = styled.div`
  background: ${COLORS.white};
  border-radius: 8px;
  border: 1px solid ${COLORS['neutral/200']};
`;

export const CompanyLogo = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${COLORS['neutral/200']};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const CompanyNameRow = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 20px;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid ${COLORS['neutral/200']};
`;

export const SectionTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${COLORS['secondary/500']};
  line-height: 1.3;
`;

export const AboutSubtitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS['primary/900']};
`;

export const AboutText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${COLORS['black']};
  line-height: 1.55;

  ol,
  ul {
    padding-left: 1.5em;
  }
`;

export const ContactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 32px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const ContactItem = styled.div<{ $centered?: boolean }>`
  display: flex;
  align-items: ${({ $centered }) => ($centered ? 'center' : 'flex-start')};
  gap: 12px;
  height: 60px;
`;

export const ContactIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${COLORS['primary/50']};
`;

export const ContactTextBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ContactLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.44;
  color: ${COLORS['primary/500']};
`;

export const ContactValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${COLORS['primary/900']};

  a {
    color: inherit;
    text-decoration: none;
    word-break: break-all;

    &:visited {
      color: inherit;
    }

    &:hover {
      color: ${COLORS['secondary/500']};
      text-decoration: underline;
    }
  }
`;

export const ActiveLocationValue = styled(ContactValue)`
  line-height: 1.4;
`;

export const ContactLink = styled.a`
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${COLORS['primary/900']};
  text-decoration: none;

  &:visited {
    color: ${COLORS['primary/900']};
  }

  &:hover {
    color: ${COLORS['secondary/500']};
    text-decoration: underline;
  }
`;

export const PricesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const PriceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${COLORS['neutral/300']};
  background: ${COLORS['neutral/50']};
`;

export const PriceCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PriceIconBox = styled.div<{ $bg?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: ${({ $bg }) => $bg ?? COLORS['primary/500']};
  flex-shrink: 0;
`;

export const PriceLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS['primary/700']};
`;

export const PriceValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${COLORS['primary/700']};
  line-height: 1;
`;

export const BannerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const BannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
`;

export const LogoWrapper = styled.div`
  position: relative;
  width: 90px;
  height: 90px;
  flex-shrink: 0;
`;

export const LogoUploadOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const QuillWrapper = styled.div`
  .ql-toolbar.ql-snow {
    border-radius: 4px 4px 0 0;
    border-color: rgba(0, 0, 0, 0.23);
    font-family: inherit;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 4px 4px;
    border-color: rgba(0, 0, 0, 0.23);
    font-family: inherit;
    font-size: 14px;
    min-height: 130px;
  }

  .ql-editor {
    min-height: 130px;
    padding: 12px;

    ol,
    ul {
      padding-left: 1.5em;
    }

    &.ql-blank::before {
      color: rgba(0, 0, 0, 0.4);
      font-style: normal;
    }
  }

  /* Match quill snow specificity to override its #06c accent */
  .ql-snow.ql-toolbar button:hover,
  .ql-snow.ql-toolbar button:focus,
  .ql-snow.ql-toolbar button.ql-active,
  .ql-snow.ql-toolbar .ql-picker-label:hover,
  .ql-snow.ql-toolbar .ql-picker-label.ql-active,
  .ql-snow.ql-toolbar .ql-picker-item:hover,
  .ql-snow.ql-toolbar .ql-picker-item.ql-selected {
    color: ${COLORS['secondary/500']};
  }

  .ql-snow.ql-toolbar button:hover .ql-stroke,
  .ql-snow.ql-toolbar button:focus .ql-stroke,
  .ql-snow.ql-toolbar button.ql-active .ql-stroke,
  .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
  .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke {
    stroke: ${COLORS['secondary/500']};
  }

  .ql-snow.ql-toolbar button:hover .ql-fill,
  .ql-snow.ql-toolbar button:focus .ql-fill,
  .ql-snow.ql-toolbar button.ql-active .ql-fill {
    fill: ${COLORS['secondary/500']};
  }

  .ql-container.ql-snow {
    overflow: visible;
  }

  .ql-tooltip {
    left: 0 !important;
    z-index: 10;
    background: white;
    white-space: nowrap;
  }

  &:focus-within {
    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      border-color: ${COLORS['secondary/500']};
    }

    .ql-toolbar.ql-snow {
      border-bottom-color: rgba(0, 0, 0, 0.23);
    }
  }
`;
